import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { TypingApi } from '../../api';
import { useCallApi } from './common';
import { getDisneyRetirementDeadline } from '../../utils/DisneyAvailability';

type Availability = 'checking' | 'available' | 'retired' | 'error';
const MAX_TIMER_DELAY = 24 * 60 * 60 * 1000;

export const useDisneyAvailability = (enabled: boolean) => {
  const { key: routeKey } = useLocation();
  const { defaultConfiguration } = useCallApi();
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState<{ routeKey: string; availability: Availability }>({ routeKey, availability: 'checking' });

  useEffect(() => {
    if (!enabled) return;
    let disposed = false;
    let retired = false;
    let sequence = 0;
    let timer: ReturnType<typeof setTimeout>;
    let controller: AbortController | undefined;
    const update = (availability: Availability) => {
      if (!disposed) setState({ routeKey, availability });
    };
    const expire = () => {
      retired = true;
      controller?.abort();
      update('retired');
    };
    const schedule = (deadline: number) => {
      clearTimeout(timer);
      const remaining = deadline - performance.now();
      if (remaining <= 0) {
        expire();
      } else {
        timer = setTimeout(() => schedule(deadline), Math.min(remaining, MAX_TIMER_DELAY));
      }
    };
    const check = async () => {
      if (retired || disposed) return;
      const request = ++sequence;
      controller?.abort();
      const abort = new AbortController();
      controller = abort;
      update('checking');
      const startedAt = performance.now();
      try {
        const api = new TypingApi(await defaultConfiguration());
        const response = await api.apiV1TypingGradesCurrentGet({ signal: abort.signal, timeout: 10000 });
        if (disposed || retired || request !== sequence) return;
        const data = response.data as { c3_retirement?: unknown };
        const deadline = getDisneyRetirementDeadline(data.c3_retirement, startedAt);
        schedule(deadline);
        if (!retired) update('available');
      } catch {
        if (!disposed && !retired && request === sequence) update('error');
      }
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible') void check();
    };
    const onResume = () => void check();
    void check();
    // Revalidate before restoring an old tab, BFCache entry or browser history page.
    window.addEventListener('focus', onResume);
    window.addEventListener('pageshow', onResume);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      disposed = true;
      controller?.abort();
      clearTimeout(timer);
      window.removeEventListener('focus', onResume);
      window.removeEventListener('pageshow', onResume);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [enabled, routeKey, attempt, defaultConfiguration]);

  return {
    availability: enabled && state.routeKey === routeKey ? state.availability : 'checking',
    retry: () => setAttempt((value) => value + 1),
  };
};
